/**
 * Seasonal Events Configuration
 * Central source of truth for all seasonal themes and events
 */

export type SeasonalEventType = 'NEW_YEAR' | 'CHRISTMAS' | 'VALENTINE' | 'HALLOWEEN' | 'EASTER' | null;

export interface SeasonalEventConfig {
  eventType: SeasonalEventType;
  displayName: string;
  isEnabled: boolean;
  startDate: Date;
  endDate: Date;
  promoCode: string | null;
  bannerMessage: string | null;
  bannerMessageDa: string | null;
  discountPercent: number | null;
  themeConfig: {
    accentColor?: string;
    showFireworks?: boolean;
    showConfetti?: boolean;
    showSnowfall?: boolean;
  };
}

export const defaultEventConfig: SeasonalEventConfig = {
  eventType: null,
  displayName: '',
  isEnabled: false,
  startDate: new Date(),
  endDate: new Date(),
  promoCode: null,
  bannerMessage: null,
  bannerMessageDa: null,
  discountPercent: null,
  themeConfig: {},
};

// New Year theme colors (HSL values)
export const newYearColors = {
  gold: "45 93% 47%",
  champagne: "39 100% 76%",
  silver: "0 0% 75%",
  midnight: "240 100% 10%",
  firework: "280 100% 60%",
  sparkle: "50 100% 50%",
};

export const newYearGradients = {
  festive: "linear-gradient(135deg, hsl(45 93% 47% / 0.15), hsl(280 100% 60% / 0.1))",
  midnight: "linear-gradient(180deg, hsl(240 100% 10% / 0.1), transparent)",
  gold: "linear-gradient(135deg, hsl(45 93% 47% / 0.2), hsl(39 100% 76% / 0.1))",
  sparkle: "linear-gradient(135deg, hsl(50 100% 50% / 0.15), hsl(45 93% 47% / 0.1))",
};

export const newYearEmojis = {
  firework: "🎆",
  party: "🎉",
  confetti: "🎊",
  champagne: "🍾",
  clock: "🕐",
  star: "✨",
  sparkles: "🎇",
  balloon: "🎈",
  gift: "🎁",
  calendar: "📅",
};

// New Year recipe badges (only available during the event)
export const newYearRecipeBadges = [
  { id: "ny-special", label: "NY Special", labelDa: "Nytårs Special", emoji: "🎉", color: "gold" },
  { id: "party-food", label: "Party Food", labelDa: "Festmad", emoji: "🥳", color: "purple" },
  { id: "midnight-snack", label: "Midnight Snack", labelDa: "Midnatssnack", emoji: "🌙", color: "indigo" },
  { id: "hangover-fix", label: "Hangover Fix", labelDa: "Tømmermandskur", emoji: "🍳", color: "green" },
  { id: "bubbly-pairing", label: "Bubbly Pairing", labelDa: "Champagne Match", emoji: "🍾", color: "amber" },
];

// Seasonal achievements that can be earned during the event
export const newYearAchievements = [
  {
    id: "ny-first-recipe",
    name: "New Year Chef",
    nameDa: "Nytårskok",
    description: "Created a recipe during the New Year event",
    descriptionDa: "Oprettede en opskrift under nytårsarrangementet",
    emoji: "🎆",
    xpReward: 100,
  },
  {
    id: "ny-party-host",
    name: "Party Host",
    nameDa: "Festvært",
    description: "Created 3 recipes with New Year badges",
    descriptionDa: "Oprettede 3 opskrifter med nytårsbadges",
    emoji: "🎉",
    xpReward: 250,
  },
  {
    id: "ny-countdown",
    name: "Countdown Champion",
    nameDa: "Nedtællingsmester",
    description: "Active on the platform on New Year's Eve",
    descriptionDa: "Aktiv på platformen nytårsaften",
    emoji: "🕐",
    xpReward: 150,
  },
];

// Logo paths
export const logos = {
  regular: '/chaos-kitchen-logo.webp',
  christmas: '/chaos-kitchen-christmas-logo.webp',
  newYear: '/chaos-kitchen-newyear-logo.webp',
  valentine: '/chaos-kitchen-valentine-logo.webp',
  halloween: '/chaos-kitchen-halloween-logo.webp',
};

/**
 * Check if we're within the New Year event period
 * This is a client-side fallback when database config isn't available
 */
export const isNewYearPeriod = (): boolean => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  // Dec 26–31 of this year, or Jan 1–2 of next
  if (month === 11) {
    const startDate = new Date(`${year}-12-26T00:00:00`);
    return now >= startDate;
  }
  if (month === 0) {
    const endDate = new Date(`${year}-01-02T23:59:59`);
    return now <= endDate;
  }
  return false;
};

export const isChristmasPeriod = (): boolean => {
  const now = new Date();
  const year = now.getFullYear();
  const startDate = new Date(`${year}-12-01T00:00:00`);
  const endDate = new Date(`${year}-12-25T23:59:59`);
  return now >= startDate && now <= endDate;
};

export const newYearRecipeThemes = [
  { value: "nye-party", label: "NYE Party Spreads", emoji: "🎉" },
  { value: "midnight-snack", label: "Midnight Snacks", emoji: "🌙" },
  { value: "hangover-brunch", label: "Hangover Brunch", emoji: "🍳" },
  { value: "bubbly-pairings", label: "Bubbly Pairings", emoji: "🍾" },
  { value: "countdown-canapes", label: "Countdown Canapés", emoji: "✨" },
  { value: "lucky-new-year", label: "Lucky New Year Dishes", emoji: "🍀" },
];

/**
 * Get the current seasonal logo based on active events
 */
export const getSeasonalLogo = (activeEvent: SeasonalEventType): string => {
  switch (activeEvent) {
    case 'NEW_YEAR':
      return logos.newYear;
    case 'CHRISTMAS':
      return logos.christmas;
    case 'VALENTINE':
      return logos.valentine;
    case 'HALLOWEEN':
      return logos.halloween;
    default:
      return logos.regular;
  }
};
